import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress
} from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { apiCall } from '../utils/api';

const AdminAIControls = () => {
  const [aiEnabled, setAiEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [error, setError] = useState('');

  const loadAIStatus = async () => {
    try {
      // First try the health endpoint (no auth required)
      const response = await apiCall('/api/ai/health', { requireAuth: false });
      
      if (response && response.data && response.data.ai_feature_enabled !== undefined) {
        setAiEnabled(response.data.ai_feature_enabled);
      } else {
        // If that doesn't have the status, try the admin endpoint (with auth)
        const adminResponse = await apiCall('/api/ai/admin/feature-status');
        setAiEnabled(adminResponse.ai_template_generation_enabled);
      }
    } catch (error) {
      console.error('Error loading AI status:', error);
      setError(`Failed to load AI feature status: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsageStats = useCallback(async () => {
    try {
      const response = await apiCall(`/api/ai/admin/usage-summary?month=${selectedMonth}`);
      setUsageStats(response);
    } catch (error) {
      console.error('Error loading usage stats:', error);
      // Don't set error for stats as this is less critical
    }
  }, [selectedMonth]);

  useEffect(() => {
    loadAIStatus();
    loadUsageStats();
  }, [loadUsageStats]);

  useEffect(() => {
    loadUsageStats();
  }, [selectedMonth, loadUsageStats]);

  const toggleAIFeature = async () => {
    setIsUpdating(true);
    setError('');

    try {
      await apiCall('/api/ai/admin/feature-toggle', {
        method: 'POST',
        body: JSON.stringify({ enabled: !aiEnabled })
      });

      setAiEnabled(!aiEnabled);
      
      // Show success message
      setError(''); // Clear any previous errors
      
    } catch (error) {
      console.error('Error toggling AI feature:', error);
      setError(error.message || 'Failed to update AI feature setting');
    } finally {
      setIsUpdating(false);
    }
  };

  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthString = date.toISOString().slice(0, 7);
      const displayName = date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long' 
      });
      
      options.push({ value: monthString, label: displayName });
    }
    
    return options;
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={48} />
            <Typography variant="h6" color="text.secondary">
              Loading AI controls...
            </Typography>
          </Stack>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SmartToyIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            AI Administration
          </Typography>
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* AI Feature Toggle */}
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    AI Template Generation
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enable or disable AI-powered template generation for users
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={aiEnabled}
                      onChange={toggleAIFeature}
                      disabled={isUpdating}
                      color="primary"
                    />
                  }
                  label={aiEnabled ? 'Enabled' : 'Disabled'}
                />
              </Box>

              {isUpdating && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Updating AI feature status...
                  </Typography>
                </Box>
              )}

              <Alert severity={aiEnabled ? 'success' : 'info'}>
                {aiEnabled 
                  ? 'AI template generation is currently enabled. Users can create AI-powered runbook templates.'
                  : 'AI template generation is currently disabled. Users cannot create AI-powered templates.'
                }
              </Alert>
            </Stack>
          </CardContent>
        </Card>

        {/* Usage Statistics */}
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Usage Statistics
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monitor AI feature usage and performance metrics
                  </Typography>
                </Box>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={loadUsageStats}
                  variant="outlined"
                  size="small"
                >
                  Refresh
                </Button>
              </Box>

              {/* Month Selector */}
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Select Month</InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  label="Select Month"
                >
                  {generateMonthOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Stats Display */}
              {usageStats ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary" gutterBottom>
                        {usageStats.total_requests || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Requests
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="secondary" gutterBottom>
                        {usageStats.successful_requests || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Successful Requests
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="error" gutterBottom>
                        {usageStats.failed_requests || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Failed Requests
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No usage statistics available for the selected month.
                  </Typography>
                </Box>
              )}

              {/* Success Rate */}
              {usageStats && usageStats.total_requests > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2">
                      Success Rate
                    </Typography>
                    <Typography variant="body2" color="primary">
                      {Math.round((usageStats.successful_requests / usageStats.total_requests) * 100)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(usageStats.successful_requests / usageStats.total_requests) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Additional Controls */}
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6" gutterBottom>
                Additional Controls
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SettingsIcon color="action" />
                        <Typography variant="subtitle2">
                          Model Configuration
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Configure AI models, API keys, and model parameters
                      </Typography>
                      <Button variant="outlined" size="small">
                        Configure Models
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon color="action" />
                        <Typography variant="subtitle2">
                          Performance Monitoring
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Monitor AI response times, costs, and quality metrics
                      </Typography>
                      <Button variant="outlined" size="small">
                        View Analytics
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};

export default AdminAIControls;
